import { useRef, useCallback, useMemo, useState } from "react";
import { showToast } from "../../../../../../core/common-service/CommonService";
import { useTranslation } from "react-i18next";
import {
  postGenerateAICover,
  postGenerateAIChapterImage,
} from "../../../../../../core/api/Api";
import {
  PLACEHOLDER_IMAGE_URL,
  MAX_IMAGE_SIZE_BYTES,
} from "../../../../../../core/constants/constants";

export const useImageUpload = (
  service_session_id,
  setCoverPhotoUrl,
  setChapterPhotosUrls,
) => {
  const { t } = useTranslation();
  const uploadedInputIdForChapter = useRef("");
  const uploadedImageForChapterFormData = useRef(new FormData());
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const [generatingChapterId, setGeneratingChapterId] = useState(null);

  const uploadImagesArr = useMemo(() => [...Array(2).keys()], []);
  const coverPhotoInputId = "cover_media_photo";
  const coverPhotoImageId = service_session_id + "-image-cover-photo";

  const otherPhotoInputId = (id, image_index) =>
    "chap_" + id + "_media_" + image_index;

  const otherPhotoImageId = useCallback(
    (index, image_index) =>
      service_session_id + "-image-" + index + "-" + image_index,
    [service_session_id],
  );

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  function base64ToBlob(base64) {
    const byteChars = atob(base64.split(",")[1]);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const mimeMatch = base64.match(/^data:([^;]+);/);
    const mimeType = mimeMatch ? mimeMatch[1] : "image/jpeg";
    return new Blob([byteArray], { type: mimeType });
  }

  const onUploadImageClick = (input_id) => (e) => {
    uploadedInputIdForChapter.current = input_id;
    document.getElementById(input_id).click();
  };

  const onUploadImageChange = (index, image_index) => async (e) => {
    let file = e.target.files[0];
    if (!file) return;

    const maxSize = MAX_IMAGE_SIZE_BYTES;
    if (file.size > maxSize) {
      showToast({
        type: "error",
        message: t("image_is_too_big"),
      });
      return;
    }

    uploadedImageForChapterFormData.current.append(
      uploadedInputIdForChapter.current,
      file,
    );
    const image_id =
      index === "cover"
        ? coverPhotoImageId // Use coverPhotoImageId for cover photos
        : otherPhotoImageId(index, image_index); // Use otherPhotoImageId for chapter photos

    const base64String = await blobToBase64(file);
    localStorage.setItem(image_id, base64String);

    var reader = new FileReader();
    reader.onload = function (e) {
      const imageUrl = e.target.result;
      document.getElementById(image_id).setAttribute("src", imageUrl);

      // Update state for preview modal
      if (uploadedInputIdForChapter.current === coverPhotoInputId) {
        // This is the cover photo
        if (setCoverPhotoUrl) {
          setCoverPhotoUrl(imageUrl);
        }
      } else {
        // This is a chapter photo - extract chapter ID from the input_id
        // Format: "chap_{id}_media_{index}"
        const match = uploadedInputIdForChapter.current.match(
          /chap_(\d+)_media_(\d+)/,
        );
        if (match && setChapterPhotosUrls) {
          const chapterId = parseInt(match[1]);
          const photoIndex = parseInt(match[2]);

          setChapterPhotosUrls((prev) => ({
            ...prev,
            [chapterId]: {
              ...prev[chapterId],
              [photoIndex]: imageUrl,
            },
          }));
        }
      }
    };
    reader.readAsDataURL(file);

    // Show the image container
    const containerElement = document.getElementById(
      "container-" + uploadedInputIdForChapter.current,
    );
    if (containerElement) {
      containerElement.classList.remove("d-none");
    }

    // Hide the upload area
    const uploadAreaElement = document.getElementById(
      "upload-area-" + uploadedInputIdForChapter.current,
    );
    if (uploadAreaElement) {
      uploadAreaElement.style.display = "none";
    }
  };

  const onRemoveImage = (input_id, image_id) => (e) => {
    e.stopPropagation();

    // Remove from localStorage
    localStorage.removeItem(image_id);

    // Remove from FormData
    uploadedImageForChapterFormData.current.delete(input_id);

    // Reset the image
    const imgElement = document.getElementById(image_id);
    if (imgElement) {
      imgElement.src = PLACEHOLDER_IMAGE_URL;
    }

    // Hide the image container
    const containerElement = document.getElementById("container-" + input_id);
    if (containerElement) {
      containerElement.classList.add("d-none");
    }

    // Show the upload area again
    const uploadAreaElement = document.getElementById(
      "upload-area-" + input_id,
    );
    if (uploadAreaElement) {
      uploadAreaElement.style.display = "flex";
    }

    // IMPORTANT: Reset the file input so onChange fires again
    const fileInput = document.getElementById(input_id);
    if (fileInput) {
      fileInput.value = ""; // ← ADD THIS LINE
    }

    // Update state for preview modal
    if (input_id === coverPhotoInputId) {
      // Removing cover photo
      if (setCoverPhotoUrl) {
        setCoverPhotoUrl(null);
      }
    } else {
      // Removing chapter photo - extract chapter ID from input_id
      const match = input_id.match(/chap_(\d+)_media_(\d+)/);
      if (match && setChapterPhotosUrls) {
        const chapterId = parseInt(match[1]);
        const photoIndex = parseInt(match[2]);

        setChapterPhotosUrls((prev) => {
          const updated = { ...prev };
          if (updated[chapterId]) {
            const chapterPhotos = { ...updated[chapterId] };
            delete chapterPhotos[photoIndex];

            // If no more photos for this chapter, remove the chapter key
            if (Object.keys(chapterPhotos).length === 0) {
              delete updated[chapterId];
            } else {
              updated[chapterId] = chapterPhotos;
            }
          }
          return updated;
        });
      }
    }
  };

  const onGenerateWithAI = useCallback(async () => {
    if (!service_session_id) {
      showToast({
        type: "error",
        message: t("session_not_found"),
      });
      return;
    }

    setIsGeneratingCover(true);

    try {
      const response = await postGenerateAICover(service_session_id);
      const result = response.data;

      if (result.success && result.data) {
        const imageSource = result.data.image_base64;

        // Save to localStorage
        localStorage.setItem(coverPhotoImageId, imageSource);

        // If we have base64, convert to blob for form data
        if (result.data.image_base64) {
          const base64Data = result.data.image_base64.split(",")[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "image/png" });

          // Add to form data
          uploadedImageForChapterFormData.current.append(
            coverPhotoInputId,
            blob,
          );
        }

        // Update UI
        const imgElement = document.getElementById(coverPhotoImageId);
        const containerElement = document.getElementById(
          "container-" + coverPhotoInputId,
        );
        const uploadAreaElement = document.getElementById(
          "upload-area-" + coverPhotoInputId,
        );

        if (imgElement) {
          imgElement.src = imageSource;
        }

        if (containerElement) {
          containerElement.classList.remove("d-none");
        }

        if (uploadAreaElement) {
          uploadAreaElement.style.display = "none";
        }

        showToast({
          type: "success",
          message: t("cover_generated_successfully"),
        });
      } else {
        showToast({
          type: "error",
          message:
            result?.message === "book.book_summary_required"
              ? t("cover_generation_unavailable")
              : t("failed_to_generate_cover"),
        });
      }
    } catch (error) {
      // Backend returns 400 with this message when the project has no
      // generated chapter to build a cover from — surface a clear, actionable
      // message instead of a generic error.
      const backendMessage = error?.response?.data?.message;
      showToast({
        type: "error",
        message:
          backendMessage === "book.book_summary_required"
            ? t("cover_generation_unavailable")
            : t("error_generating_cover"),
      });
    } finally {
      setIsGeneratingCover(false);
    }
  }, [service_session_id, coverPhotoImageId, coverPhotoInputId, t]);

  const onGenerateChapterImageWithAI = useCallback(
    async (chapterItem, chapterIndex) => {
      if (!service_session_id) {
        showToast({ type: "error", message: t("session_not_found") });
        return;
      }

      // Find the next available image slot for this chapter
      let nextSlot = -1;
      for (let i = 0; i < uploadImagesArr.length; i++) {
        const imageElement = document.getElementById(
          otherPhotoImageId(chapterIndex, i),
        );
        if (!imageElement || imageElement.src.includes("placehold.co")) {
          nextSlot = i;
          break;
        }
      }

      if (nextSlot === -1) {
        showToast({ type: "error", message: t("no_image_slot_available") });
        return;
      }

      setGeneratingChapterId(chapterItem.id);

      try {
        const response = await postGenerateAIChapterImage(
          service_session_id,
          chapterItem.id,
        );
        const result = response.data;

        if (result.success && result.data) {
          const imageSource = result.data.image_base64;
          const image_id = otherPhotoImageId(chapterIndex, nextSlot);
          const input_id = otherPhotoInputId(chapterItem.id, nextSlot);

          // Save to localStorage
          localStorage.setItem(image_id, imageSource);

          // Convert base64 to blob for form data
          if (result.data.image_base64) {
            const base64Data = result.data.image_base64.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });

            uploadedImageForChapterFormData.current.append(input_id, blob);
          }

          // Update UI
          const imgElement = document.getElementById(image_id);
          const containerElement = document.getElementById(
            "container-" + input_id,
          );

          if (imgElement) {
            imgElement.src = imageSource;
          }

          if (containerElement) {
            containerElement.classList.remove("d-none");
          }

          // Update chapterPhotosUrls state
          if (setChapterPhotosUrls) {
            setChapterPhotosUrls((prev) => ({
              ...prev,
              [chapterItem.id]: {
                ...prev[chapterItem.id],
                [nextSlot]: imageSource,
              },
            }));
          }

          showToast({
            type: "success",
            message: t("chapter_image_generated_successfully"),
          });
        } else {
          showToast({
            type: "error",
            message: t("failed_to_generate_chapter_image"),
          });
        }
      } catch (error) {
        console.error("Error generating chapter image:", error);
        showToast({
          type: "error",
          message: t("error_generating_chapter_image"),
        });
      } finally {
        setGeneratingChapterId(null);
      }
    },
    [
      service_session_id,
      uploadImagesArr,
      otherPhotoImageId,
      otherPhotoInputId,
      uploadedImageForChapterFormData,
      setChapterPhotosUrls,
      t,
    ],
  );

  const loadCachedImages = useCallback(
    async (data) => {
      if (coverPhotoImageId) {
        const inputEl = document.getElementById(coverPhotoImageId);
        if (inputEl) {
          const base64String = await localStorage.getItem(coverPhotoImageId);
          if (base64String) {
            document.getElementById(coverPhotoImageId).src = base64String;
            const file = await base64ToBlob(base64String);
            uploadedImageForChapterFormData.current.append(
              coverPhotoInputId,
              file,
            );
            document
              .getElementById("container-" + coverPhotoInputId)
              .classList.remove("d-none");

            // Hide upload area if image exists
            const uploadAreaElement = document.getElementById(
              "upload-area-" + coverPhotoInputId,
            );
            if (uploadAreaElement) {
              uploadAreaElement.style.display = "none";
            }

            // Update state for preview modal
            if (setCoverPhotoUrl) {
              setCoverPhotoUrl(base64String);
            }
          }
        }
      }

      data.forEach(async (d, i) => {
        await uploadImagesArr.forEach(async (el_image, image_index) => {
          const image_id = otherPhotoImageId(i, image_index);
          if (image_id) {
            const inputEl = document.getElementById(image_id);
            if (inputEl) {
              const base64String = await localStorage.getItem(image_id);
              if (base64String) {
                document.getElementById(image_id).src = base64String;
                const file = await base64ToBlob(base64String);
                uploadedImageForChapterFormData.current.append(
                  "chap_" + d?.id + "_media_" + image_index,
                  file,
                );
                document
                  .getElementById(
                    "container-chap_" + d?.id + "_media_" + image_index,
                  )
                  .classList.remove("d-none");

                // Update state for preview modal
                if (setChapterPhotosUrls) {
                  setChapterPhotosUrls((prev) => ({
                    ...prev,
                    [d.id]: {
                      ...prev[d.id],
                      [image_index]: base64String,
                    },
                  }));
                }
              }
            }
          }
        });
      });
    },
    [
      coverPhotoImageId,
      otherPhotoImageId,
      uploadImagesArr,
      setCoverPhotoUrl,
      setChapterPhotosUrls,
      coverPhotoInputId,
    ],
  );

  const appendCachedImagesToFormData = useCallback(
    (formData, data) => {
      // Cover photo from localStorage
      const coverBase64 = localStorage.getItem(coverPhotoImageId);
      if (coverBase64) {
        const blob = base64ToBlob(coverBase64);
        formData.append(coverPhotoInputId, blob, "cover.jpg");
      }

      // Chapter photos from localStorage
      if (data && Array.isArray(data)) {
        data.forEach((d, i) => {
          uploadImagesArr.forEach((_, image_index) => {
            const image_id = otherPhotoImageId(i, image_index);
            const base64String = localStorage.getItem(image_id);
            if (base64String) {
              const blob = base64ToBlob(base64String);
              formData.append(
                "chap_" + d?.id + "_media_" + image_index,
                blob,
                "chapter_img_" + i + "_" + image_index + ".jpg",
              );
            }
          });
        });
      }
    },
    [coverPhotoImageId, coverPhotoInputId, otherPhotoImageId, uploadImagesArr],
  );

  return {
    uploadedInputIdForChapter,
    uploadedImageForChapterFormData,
    uploadImagesArr,
    coverPhotoInputId,
    coverPhotoImageId,
    otherPhotoInputId,
    otherPhotoImageId,
    onUploadImageClick,
    onUploadImageChange,
    onRemoveImage,
    onGenerateWithAI,
    isGeneratingCover,
    onGenerateChapterImageWithAI,
    generatingChapterId,
    loadCachedImages,
    appendCachedImagesToFormData,
  };
};
